// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

export const ACTIVITY_IDLE_MS = 5 * 60 * 1000;
export const ACTIVITY_HEARTBEAT_MIN_INTERVAL_MS = 60 * 1000;

const INPUT_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

let lastInputAt = Date.now();

function isVisible(): boolean {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

export function noteUserInput(now: number = Date.now()): void {
  lastInputAt = now;
}

export function isUserActive(now: number = Date.now()): boolean {
  return isVisible() && now - lastInputAt < ACTIVITY_IDLE_MS;
}

export interface ActivityHeartbeatOptions {
  send: () => Promise<unknown>;
  now?: () => number;
  target?: Window;
}

export function startActivityHeartbeat({
  send,
  now = Date.now,
  target = window,
}: ActivityHeartbeatOptions): () => void {
  let lastSentAt = Number.NEGATIVE_INFINITY;

  const onInput = () => {
    const at = now();
    noteUserInput(at);
    if (!isVisible() || at - lastSentAt < ACTIVITY_HEARTBEAT_MIN_INTERVAL_MS) {
      return;
    }
    lastSentAt = at;
    void send().catch(() => undefined);
  };

  const onVisibility = () => {
    if (isVisible()) {
      onInput();
    }
  };

  for (const name of INPUT_EVENTS) {
    target.addEventListener(name, onInput, { passive: true });
  }
  target.addEventListener("focus", onInput);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    for (const name of INPUT_EVENTS) {
      target.removeEventListener(name, onInput);
    }
    target.removeEventListener("focus", onInput);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
